import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, LOGO_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { to12h, SLOTS } from "@/lib/slots";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  CalendarDays, Receipt, FileText, Building2, LogOut, Trash2, ExternalLink, Plus, ListChecks, Mail, CalendarOff,
} from "lucide-react";

const TABS = [
  { id: "appointments", label: "Appointments", icon: CalendarDays },
  { id: "checklist", label: "To-Do Checklist", icon: ListChecks },
  { id: "blocks", label: "Block Times", icon: CalendarOff },
  { id: "tax", label: "Tax Portal", icon: Receipt },
  { id: "grants", label: "Grant Calendar", icon: FileText },
  { id: "requests", label: "Service Requests", icon: Building2 },
];

const badge = (status) => {
  const map = {
    pending: "bg-[#FBEFD6] text-[#B88E3E]",
    booked: "bg-[#E5DEEB] text-[#6B4C8A]", confirmed: "bg-[#DCEEE3] text-[#3E7B5A]",
    completed: "bg-[#E5DEEB] text-[#5C5166]", cancelled: "bg-[#F6E2E2] text-[#964545]",
    rejected: "bg-[#F6E2E2] text-[#964545]", rescheduled: "bg-[#E5DEEB] text-[#6B4C8A]",
    new: "bg-[#DCEEE3] text-[#3E7B5A]", reviewing: "bg-[#FBEFD6] text-[#B88E3E]",
    scheduled: "bg-[#E5DEEB] text-[#6B4C8A]", declined: "bg-[#F6E2E2] text-[#964545]",
  };
  return map[status] || "bg-[#E5DEEB] text-[#5C5166]";
};

export default function AdminDashboard() {
  const { user, checking, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("appointments");

  useEffect(() => {
    if (!checking && !user) navigate("/admin");
  }, [checking, user, navigate]);

  if (checking || !user) return <div className="min-h-screen flex items-center justify-center text-[#6B4C8A]">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#FCFBFC] lg:flex">
      {/* Mobile: feature dropdown */}
      <div className="lg:hidden sticky top-0 z-40 bg-white border-b border-[#E5DEEB] px-4 py-3 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="UDC" className="h-8" />
            <span className="font-serif text-lg text-[#2D2336]">Admin</span>
          </div>
          <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn-mobile"
            className="flex items-center gap-1.5 text-sm font-medium text-[#964545]">
            <LogOut className="h-4 w-4" /> Sign Out
          </button>
        </div>
        <Select value={tab} onValueChange={setTab}>
          <SelectTrigger data-testid="admin-feature-dropdown" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TABS.map((t) => (
              <SelectItem key={t.id} value={t.id} data-testid={`feature-option-${t.id}`}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 bg-white border-r border-[#E5DEEB] flex-col fixed h-screen">
        <div className="p-6 border-b border-[#E5DEEB] flex items-center gap-3">
          <img src={LOGO_URL} alt="UDC" className="h-10" />
          <span className="font-serif text-lg text-[#2D2336]">Admin</span>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                tab === t.id ? "bg-[#6B4C8A] text-white" : "text-[#5C5166] hover:bg-[#F3EFF7]"
              }`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-[#E5DEEB]">
          <p className="text-xs text-[#78698A] px-2 mb-2 truncate">{user.email}</p>
          <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-[#964545] hover:bg-[#F6E2E2] transition-colors">
            <LogOut className="h-4 w-4" /> Sign Out
          </button>
        </div>
      </aside>

      <main className="lg:ml-64 flex-1 p-6 lg:p-10">
        {tab === "appointments" && <AppointmentsTab />}
        {tab === "checklist" && <ChecklistTab />}
        {tab === "blocks" && <BlocksTab />}
        {tab === "tax" && <TaxTab />}
        {tab === "grants" && <GrantsTab />}
        {tab === "requests" && <RequestsTab />}
      </main>
    </div>
  );
}

function Header({ title, subtitle }) {
  return (
    <div className="mb-8">
      <h1 className="font-serif text-4xl text-[#2D2336]">{title}</h1>
      <p className="text-[#5C5166] mt-2">{subtitle}</p>
    </div>
  );
}

/* ---------------- Appointments ---------------- */
function AppointmentsTab() {
  const [items, setItems] = useState([]);
  const [rescheduleId, setRescheduleId] = useState(null);
  const [rDate, setRDate] = useState("");
  const [rTime, setRTime] = useState("");
  const load = () => api.get("/admin/appointments").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const act = async (id, body, msg) => {
    try {
      await api.patch(`/admin/appointments/${id}`, body);
      if (msg) toast.success(msg);
      setRescheduleId(null); setRDate(""); setRTime("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    }
  };

  const openReschedule = (a) => {
    setRescheduleId(a.id); setRDate(a.appointment_date); setRTime(a.appointment_time);
  };
  const saveReschedule = (id) => {
    if (!rDate || !rTime) return toast.error("Pick a new date and time");
    act(id, { appointment_date: rDate, appointment_time: rTime, status: "rescheduled" }, "Appointment rescheduled");
  };

  return (
    <div>
      <Header title="Appointments" subtitle="Requests are pending until you approve, reject, or reschedule them. Patient details are visible only to admins." />
      <div className="space-y-4" data-testid="appointments-list">
        {items.length === 0 && <div className="bg-white border border-[#E5DEEB] rounded-2xl p-10 text-center text-[#78698A]">No appointments yet.</div>}
        {items.map((a) => (
          <div key={a.id} className="bg-white border border-[#E5DEEB] rounded-2xl p-5 shadow-sm" data-testid={`appt-${a.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-medium text-[#2D2336] text-lg">{a.full_name}</h4>
                  <span className={`text-xs rounded-full px-3 py-1 font-medium ${badge(a.status)}`} data-testid={`appt-badge-${a.id}`}>{a.status}</span>
                </div>
                <p className="text-sm text-[#5C5166] mt-1">
                  {a.appointment_date} at {to12h(a.appointment_time)} · DOB {a.date_of_birth}
                </p>
                <p className="text-sm text-[#5C5166] mt-1">{a.dental_concerns}</p>
                <p className="text-sm mt-1">
                  {a.has_insurance
                    ? <span className="text-[#5C5166]">Insurance: {a.insurance_provider || "—"} {a.insurance_member_id && `(${a.insurance_member_id})`}</span>
                    : <span className="text-[#B88E3E] font-medium">No insurance → Mobile Care</span>}
                </p>
                {(a.email || a.phone) && <p className="text-xs text-[#78698A] mt-1">{a.email} {a.phone && `· ${a.phone}`}</p>}
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => act(a.id, { status: "confirmed" }, "Appointment approved")} data-testid={`appt-approve-${a.id}`}
                  className="text-xs font-medium px-4 py-2 rounded-full bg-[#DCEEE3] text-[#3E7B5A] hover:opacity-80 transition-opacity">Approve</button>
                <button onClick={() => openReschedule(a)} data-testid={`appt-reschedule-${a.id}`}
                  className="text-xs font-medium px-4 py-2 rounded-full bg-[#E5DEEB] text-[#6B4C8A] hover:opacity-80 transition-opacity">Reschedule</button>
                <button onClick={() => act(a.id, { status: "rejected" }, "Appointment rejected")} data-testid={`appt-reject-${a.id}`}
                  className="text-xs font-medium px-4 py-2 rounded-full bg-[#F6E2E2] text-[#964545] hover:opacity-80 transition-opacity">Reject</button>
              </div>
            </div>

            {rescheduleId === a.id && (
              <div className="mt-4 pt-4 border-t border-[#E5DEEB] flex flex-wrap items-end gap-3" data-testid={`appt-reschedule-form-${a.id}`}>
                <div>
                  <Label className="text-xs text-[#78698A]">New Date</Label>
                  <Input type="date" value={rDate} onChange={(e) => setRDate(e.target.value)} data-testid={`reschedule-date-${a.id}`} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-[#78698A]">New Time</Label>
                  <select value={rTime} onChange={(e) => setRTime(e.target.value)} data-testid={`reschedule-time-${a.id}`}
                    className="mt-1 block h-10 rounded-md border border-[#E5DEEB] bg-white px-3 text-sm">
                    <option value="">Select…</option>
                    {SLOTS.map((s) => <option key={s} value={s}>{to12h(s)}</option>)}
                  </select>
                </div>
                <button onClick={() => saveReschedule(a.id)} data-testid={`reschedule-save-${a.id}`}
                  className="h-10 px-5 rounded-full bg-[#6B4C8A] text-white text-sm font-medium hover:bg-[#543C6E] transition-colors">Save</button>
                <button onClick={() => setRescheduleId(null)} className="h-10 px-4 text-sm text-[#78698A] hover:text-[#6B4C8A]">Cancel</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Tax ---------------- */
function TaxTab() {
  const [income, setIncome] = useState([]);
  const [summary, setSummary] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [rate, setRate] = useState(0.153);
  const [form, setForm] = useState({ month: "", gross_income: "", expenses: "" });

  const load = () => {
    api.get("/admin/income").then((r) => setIncome(r.data)).catch(() => {});
    api.get(`/admin/tax/summary?year=${year}&rate=${rate}`).then((r) => setSummary(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, rate]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.month) return toast.error("Select a month");
    try {
      await api.post("/admin/income", {
        month: form.month,
        gross_income: parseFloat(form.gross_income || "0"),
        expenses: parseFloat(form.expenses || "0"),
      });
      setForm({ month: "", gross_income: "", expenses: "" });
      toast.success("Income logged");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const del = async (month) => { await api.delete(`/admin/income/${month}`); load(); };
  const money = (n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div>
      <Header title="Tax Portal" subtitle="Log monthly income — quarterly estimated taxes are calculated automatically." />

      <div className="grid lg:grid-cols-3 gap-6">
        <form onSubmit={save} className="bg-white border border-[#E5DEEB] rounded-2xl p-6 shadow-sm space-y-4" data-testid="income-form">
          <h3 className="font-serif text-xl text-[#2D2336]">Log Monthly Income</h3>
          <div>
            <Label className="text-[#2D2336]">Month</Label>
            <Input type="month" data-testid="income-month" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} className="mt-1" />
          </div>
          <div>
            <Label className="text-[#2D2336]">Gross Income ($)</Label>
            <Input type="number" step="0.01" data-testid="income-gross" value={form.gross_income} onChange={(e) => setForm({ ...form, gross_income: e.target.value })} className="mt-1" />
          </div>
          <div>
            <Label className="text-[#2D2336]">Expenses ($)</Label>
            <Input type="number" step="0.01" data-testid="income-expenses" value={form.expenses} onChange={(e) => setForm({ ...form, expenses: e.target.value })} className="mt-1" />
          </div>
          <button type="submit" data-testid="income-save" className="w-full bg-[#6B4C8A] text-white py-2.5 rounded-full font-medium hover:bg-[#543C6E] transition-colors flex items-center justify-center gap-2">
            <Plus className="h-4 w-4" /> Save Income
          </button>
        </form>

        <div className="lg:col-span-2 bg-white border border-[#E5DEEB] rounded-2xl p-6 shadow-sm">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
            <h3 className="font-serif text-xl text-[#2D2336]">Quarterly Estimated Taxes</h3>
            <div className="flex gap-3">
              <div>
                <Label className="text-xs text-[#78698A]">Year</Label>
                <Input type="number" data-testid="tax-year" value={year} onChange={(e) => setYear(parseInt(e.target.value || "2026"))} className="w-24 mt-1" />
              </div>
              <div>
                <Label className="text-xs text-[#78698A]">Tax Rate</Label>
                <Input type="number" step="0.001" data-testid="tax-rate" value={rate} onChange={(e) => setRate(parseFloat(e.target.value || "0"))} className="w-24 mt-1" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="tax-summary">
            {summary?.quarters.map((q) => (
              <div key={q.quarter} className="bg-[#F9F8FA] border border-[#E5DEEB] rounded-xl p-4">
                <p className="label text-xs uppercase tracking-wide text-[#78698A] font-semibold">Q{q.quarter}</p>
                <p className="text-xs text-[#78698A] mt-2">Net</p>
                <p className="text-lg font-semibold text-[#2D2336]">{money(q.net_income)}</p>
                <p className="text-xs text-[#78698A] mt-2">Est. Tax</p>
                <p className="text-lg font-semibold text-[#6B4C8A]">{money(q.estimated_tax)}</p>
              </div>
            ))}
          </div>
          {summary && (
            <div className="mt-5 flex flex-wrap items-center justify-between gap-4 bg-[#F3EFF7] rounded-xl p-4">
              <div>
                <span className="text-sm text-[#5C5166]">Annual Net: <strong className="text-[#2D2336]">{money(summary.annual_net)}</strong></span>
                <span className="ml-4 text-sm text-[#5C5166]">Annual Est. Tax: <strong className="text-[#6B4C8A]">{money(summary.annual_estimated_tax)}</strong></span>
              </div>
              <a href="https://tap.state.nm.us/TAP/_/" target="_blank" rel="noopener noreferrer" data-testid="nm-tap-link"
                className="flex items-center gap-2 bg-[#6B4C8A] text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-[#543C6E] transition-colors">
                File on NM TAP <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 bg-white border border-[#E5DEEB] rounded-2xl overflow-hidden shadow-sm">
        <table className="w-full text-sm" data-testid="income-table">
          <thead className="bg-[#F3EFF7] text-[#5C5166]">
            <tr>{["Month", "Gross", "Expenses", "Net", ""].map((h) => <th key={h} className="text-left px-4 py-3 font-semibold">{h}</th>)}</tr>
          </thead>
          <tbody>
            {income.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-[#78698A]">No income logged yet.</td></tr>}
            {income.map((e) => (
              <tr key={e.month} className="border-t border-[#E5DEEB]">
                <td className="px-4 py-3 font-medium">{e.month}</td>
                <td className="px-4 py-3">{money(e.gross_income)}</td>
                <td className="px-4 py-3">{money(e.expenses)}</td>
                <td className="px-4 py-3 font-medium text-[#6B4C8A]">{money(e.gross_income - e.expenses)}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => del(e.month)} data-testid={`income-del-${e.month}`} className="text-[#964545] hover:opacity-70"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------- Grants ---------------- */
function GrantsTab() {
  const [grants, setGrants] = useState([]);
  const [form, setForm] = useState({ name: "", link: "", amount: "", due_date: "", notes: "" });
  const load = () => api.get("/admin/grants").then((r) => setGrants(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/grants", { ...form, amount: parseFloat(form.amount || "0") });
      setForm({ name: "", link: "", amount: "", due_date: "", notes: "" });
      toast.success("Grant added — reminders scheduled");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/admin/grants/${id}`); load(); };

  const daysUntil = (d) => Math.ceil((new Date(d + "T00:00:00") - new Date()) / 86400000);
  const statusColor = (days) => {
    if (days < 0) return "bg-[#E5DEEB] text-[#5C5166]";
    if (days <= 2) return "bg-[#F6E2E2] text-[#964545]";
    if (days <= 7) return "bg-[#FBEFD6] text-[#B88E3E]";
    if (days <= 60) return "bg-[#DCEEE3] text-[#3E7B5A]";
    return "bg-[#E5DEEB] text-[#6B4C8A]";
  };

  return (
    <div>
      <Header title="Grant Calendar" subtitle="Log grants with due dates. Automatic email reminders go out at 2-month, 1-month, 1-week and 2-day marks." />
      <div className="bg-[#F3EFF7] border border-[#C8B3D6] rounded-xl p-4 mb-6 text-sm text-[#5C5166]">
        Reminders are emailed to <strong>utopiamobilecare@gmail.com</strong>, <strong>barkleydarly@gmail.com</strong>, and <strong>mjb.zeal@gmail.com</strong>.
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <form onSubmit={save} className="bg-white border border-[#E5DEEB] rounded-2xl p-6 shadow-sm space-y-4" data-testid="grant-form">
          <h3 className="font-serif text-xl text-[#2D2336]">Add Grant</h3>
          <div><Label className="text-[#2D2336]">Grant Name</Label><Input required data-testid="grant-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-[#2D2336]">Link</Label><Input data-testid="grant-link" placeholder="https://…" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-[#2D2336]">Amount ($)</Label><Input type="number" step="0.01" data-testid="grant-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-[#2D2336]">Date Due</Label><Input required type="date" data-testid="grant-due" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-[#2D2336]">Notes</Label><Textarea rows={2} data-testid="grant-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" /></div>
          <button type="submit" data-testid="grant-save" className="w-full bg-[#6B4C8A] text-white py-2.5 rounded-full font-medium hover:bg-[#543C6E] transition-colors flex items-center justify-center gap-2"><Plus className="h-4 w-4" /> Add Grant</button>
        </form>

        <div className="lg:col-span-2 space-y-4" data-testid="grants-list">
          {grants.length === 0 && <div className="bg-white border border-[#E5DEEB] rounded-2xl p-10 text-center text-[#78698A]">No grants logged yet.</div>}
          {grants.map((g) => {
            const days = daysUntil(g.due_date);
            return (
              <div key={g.id} className="bg-white border border-[#E5DEEB] rounded-2xl p-6 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h4 className="font-serif text-xl text-[#2D2336]">{g.name}</h4>
                    <p className="text-sm text-[#5C5166] mt-1">Due {g.due_date} · {g.amount ? `$${Number(g.amount).toLocaleString()}` : "—"}</p>
                    {g.link && <a href={g.link} target="_blank" rel="noopener noreferrer" className="text-sm text-[#6B4C8A] hover:underline flex items-center gap-1 mt-1">Open link <ExternalLink className="h-3 w-3" /></a>}
                    {g.notes && <p className="text-sm text-[#78698A] mt-2">{g.notes}</p>}
                    {g.reminders_sent?.length > 0 && <p className="text-xs text-[#3E7B5A] mt-2">Reminders sent: {g.reminders_sent.join(", ")}</p>}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`text-xs rounded-full px-3 py-1.5 font-medium ${statusColor(days)}`}>
                      {days < 0 ? "Past due" : days === 0 ? "Due today" : `${days} days left`}
                    </span>
                    <button onClick={() => del(g.id)} data-testid={`grant-del-${g.id}`} className="text-[#964545] hover:opacity-70"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Service Requests ---------------- */
function RequestsTab() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/service-requests").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => { await api.patch(`/admin/service-requests/${id}`, { status }); load(); };

  return (
    <div>
      <Header title="Service Requests" subtitle="Requests from community organizations, non-profits, and businesses." />
      <div className="grid md:grid-cols-2 gap-6" data-testid="requests-list">
        {items.length === 0 && <div className="bg-white border border-[#E5DEEB] rounded-2xl p-10 text-center text-[#78698A]">No service requests yet.</div>}
        {items.map((r) => (
          <div key={r.id} className="bg-white border border-[#E5DEEB] rounded-2xl p-6 shadow-sm">
            <div className="flex items-start justify-between">
              <div>
                <h4 className="font-serif text-xl text-[#2D2336]">{r.organization_name}</h4>
                <p className="text-xs uppercase tracking-wide text-[#78698A] font-semibold mt-1">{r.org_type}</p>
              </div>
              <select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)} data-testid={`req-status-${r.id}`}
                className={`text-xs rounded-full px-3 py-1.5 font-medium border-0 ${badge(r.status)}`}>
                {["new", "reviewing", "scheduled", "declined"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="mt-4 space-y-1 text-sm text-[#5C5166]">
              <p><strong>Date/Time:</strong> {r.requested_date} at {r.requested_time}</p>
              <p><strong>Potential patients:</strong> {r.potential_patients}</p>
              <p><strong>Location:</strong> {r.location}</p>
              <p><strong>Contact:</strong> {r.contact_name} · {r.contact_email} {r.contact_phone && `· ${r.contact_phone}`}</p>
              {r.notes && <p className="text-[#78698A] pt-1">{r.notes}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


/* ---------------- To-Do Checklist ---------------- */
const TASK_TYPES = [
  { id: "grant", label: "Grant" },
  { id: "claims", label: "Claim" },
  { id: "appointment", label: "Appointment" },
  { id: "email", label: "Email Draft" },
  { id: "notes", label: "General Note" },
];

const EMPTY = {
  grant: { grant_name: "", due_date: "", website_link: "" },
  claims: { first_name: "", last_name: "", date_of_birth: "", insurance_id: "", dental_codes: [] },
  appointment: { first_name: "", last_name: "", number: "", appointment_date: "", notes: "" },
  email: { full_name: "", contact_email: "", message: "" },
  notes: { note: "" },
};

function ChecklistTab() {
  const [type, setType] = useState("grant");
  const [data, setData] = useState(EMPTY.grant);
  const [codeInput, setCodeInput] = useState("");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/todos").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const pickType = (t) => { setType(t); setData(EMPTY[t]); setCodeInput(""); };
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const addCode = () => {
    const c = codeInput.trim();
    if (!c) return;
    set("dental_codes", [...(data.dental_codes || []), c]);
    setCodeInput("");
  };
  const editCode = (i, v) => set("dental_codes", data.dental_codes.map((c, idx) => (idx === i ? v : c)));
  const removeCode = (i) => set("dental_codes", data.dental_codes.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/admin/todos", { type, data });
      toast.success(res.data.email_sent ? "Task added — emailed & texted to the team" : "Task added");
      pickType(type);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add task");
    } finally { setBusy(false); }
  };

  const toggle = async (t) => { await api.patch(`/admin/todos/${t.id}`, { done: !t.done }); load(); };
  const del = async (id) => { await api.delete(`/admin/todos/${id}`); load(); };

  const field = (k, label, props = {}) => (
    <div>
      <Label className="text-[#2D2336]">{label}</Label>
      <Input data-testid={`todo-${k}`} value={data[k] || ""} onChange={(e) => set(k, e.target.value)} className="mt-1" {...props} />
    </div>
  );

  return (
    <div>
      <Header title="To-Do Checklist" subtitle="Add tasks by type. Every submitted task is emailed to mjb.zeal@gmail.com and texted to (505) 800-8588." />

      <div className="grid lg:grid-cols-3 gap-6">
        <form onSubmit={submit} className="bg-white border border-[#E5DEEB] rounded-2xl p-6 shadow-sm space-y-4" data-testid="todo-form">
          <div>
            <Label className="text-[#2D2336]">Task Type</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {TASK_TYPES.map((t) => (
                <button type="button" key={t.id} onClick={() => pickType(t.id)} data-testid={`todo-type-${t.id}`}
                  className={`py-2 rounded-full text-sm font-medium border transition-colors ${
                    type === t.id ? "bg-[#6B4C8A] text-white border-[#6B4C8A]" : "bg-white text-[#5C5166] border-[#E5DEEB] hover:border-[#6B4C8A]"
                  }`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {type === "grant" && (
            <>
              {field("grant_name", "Grant Name", { required: true })}
              <div>
                <Label className="text-[#2D2336]">Due Date</Label>
                <Input type="date" data-testid="todo-due_date" value={data.due_date} onChange={(e) => set("due_date", e.target.value)} className="mt-1" required />
              </div>
              {field("website_link", "Website Link", { placeholder: "https://…" })}
            </>
          )}

          {type === "claims" && (
            <>
              {field("first_name", "First Name", { required: true })}
              {field("last_name", "Last Name", { required: true })}
              <div>
                <Label className="text-[#2D2336]">Date of Birth</Label>
                <Input type="date" data-testid="todo-date_of_birth" value={data.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} className="mt-1" required />
              </div>
              {field("insurance_id", "Insurance ID", { required: true })}
              <div>
                <Label className="text-[#2D2336]">Dental Codes</Label>
                <div className="flex gap-2 mt-1">
                  <Input data-testid="todo-code-input" value={codeInput} onChange={(e) => setCodeInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCode(); } }} placeholder="e.g. D1110" />
                  <button type="button" onClick={addCode} data-testid="todo-add-code" className="shrink-0 bg-[#E5DEEB] text-[#6B4C8A] px-4 rounded-lg font-medium hover:bg-[#C8B3D6] transition-colors">
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                <div className="space-y-2 mt-2" data-testid="todo-codes-list">
                  {(data.dental_codes || []).map((c, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <Input value={c} onChange={(e) => editCode(i, e.target.value)} data-testid={`todo-code-${i}`} className="text-sm" />
                      <button type="button" onClick={() => removeCode(i)} data-testid={`todo-code-del-${i}`} className="text-[#964545] hover:opacity-70"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
                  {(!data.dental_codes || data.dental_codes.length === 0) && <p className="text-xs text-[#A69CAF]">No codes added yet.</p>}
                </div>
              </div>
            </>
          )}

          {type === "appointment" && (
            <>
              {field("first_name", "First Name", { required: true })}
              {field("last_name", "Last Name", { required: true })}
              {field("number", "Phone Number", { required: true })}
              <div>
                <Label className="text-[#2D2336]">Date of Appointment</Label>
                <Input type="date" data-testid="todo-appointment_date" value={data.appointment_date} onChange={(e) => set("appointment_date", e.target.value)} className="mt-1" required />
              </div>
              <div>
                <Label className="text-[#2D2336]">Notes</Label>
                <Textarea rows={3} data-testid="todo-notes" value={data.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1" />
              </div>
            </>
          )}

          {type === "email" && (
            <>
              {field("full_name", "Full Name", { required: true })}
              {field("contact_email", "Contact Email", { type: "email", required: true })}
              <div>
                <Label className="text-[#2D2336]">Message to Draft</Label>
                <Textarea rows={4} data-testid="todo-message" value={data.message} onChange={(e) => set("message", e.target.value)} className="mt-1" required />
              </div>
            </>
          )}

          {type === "notes" && (
            <div>
              <Label className="text-[#2D2336]">General Note</Label>
              <Textarea rows={8} data-testid="todo-note" value={data.note} onChange={(e) => set("note", e.target.value)}
                spellCheck={true} className="mt-1" placeholder="Write anything — no character limit. Spell check is on." required />
            </div>
          )}

          <button type="submit" disabled={busy} data-testid="todo-submit"
            className="w-full bg-[#6B4C8A] text-white py-2.5 rounded-full font-medium hover:bg-[#543C6E] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            <Plus className="h-4 w-4" /> {busy ? "Adding…" : "Add Task"}
          </button>
        </form>

        <div className="lg:col-span-2 space-y-3" data-testid="todo-list">
          {items.length === 0 && <div className="bg-white border border-[#E5DEEB] rounded-2xl p-10 text-center text-[#78698A]">No tasks yet.</div>}
          {items.map((t) => (
            <div key={t.id} className={`bg-white border border-[#E5DEEB] rounded-2xl p-5 shadow-sm flex items-start gap-4 ${t.done ? "opacity-60" : ""}`}>
              <input type="checkbox" checked={t.done} onChange={() => toggle(t)} data-testid={`todo-done-${t.id}`}
                className="mt-1 h-5 w-5 accent-[#6B4C8A] cursor-pointer" />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs rounded-full px-2.5 py-1 font-medium bg-[#E5DEEB] text-[#6B4C8A] uppercase tracking-wide">{t.type}</span>
                  <h4 className={`font-medium text-[#2D2336] ${t.done ? "line-through" : ""}`}>{t.title}</h4>
                </div>
                <div className="mt-2 text-sm text-[#5C5166] space-y-0.5">
                  {t.type === "grant" && <>
                    <p>Due: {t.data.due_date || "—"}</p>
                    {t.data.website_link && <a href={t.data.website_link} target="_blank" rel="noopener noreferrer" className="text-[#6B4C8A] hover:underline inline-flex items-center gap-1">Link <ExternalLink className="h-3 w-3" /></a>}
                  </>}
                  {t.type === "claims" && <>
                    <p>DOB: {t.data.date_of_birth} · Insurance ID: {t.data.insurance_id}</p>
                    <p>Codes: {(t.data.dental_codes || []).join(", ") || "—"}</p>
                  </>}
                  {t.type === "appointment" && <>
                    <p>{t.data.appointment_date} · {t.data.number}</p>
                    {t.data.notes && <p className="text-[#78698A]">{t.data.notes}</p>}
                  </>}
                  {t.type === "email" && <>
                    <p className="flex items-center gap-1"><Mail className="h-3 w-3" /> {t.data.contact_email}</p>
                    <p className="text-[#78698A]">{t.data.message}</p>
                  </>}
                  {t.type === "notes" && <p className="text-[#78698A] whitespace-pre-wrap">{t.data.note}</p>}
                </div>
              </div>
              <button onClick={() => del(t.id)} data-testid={`todo-del-${t.id}`} className="text-[#964545] hover:opacity-70"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


/* ---------------- Block Times ---------------- */
const todayISO = () => new Date().toISOString().split("T")[0];

function BlocksTab() {
  const [blocks, setBlocks] = useState([]);
  const [mode, setMode] = useState("all_day"); // all_day | slot
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [reason, setReason] = useState("");
  const load = () => api.get("/admin/blocks").then((r) => setBlocks(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!date) return toast.error("Pick a date");
    if (mode === "slot" && !time) return toast.error("Pick a time");
    try {
      await api.post("/admin/blocks", {
        date, all_day: mode === "all_day",
        time: mode === "slot" ? time : null, reason,
      });
      toast.success("Time blocked");
      setDate(""); setTime(""); setReason("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not block time");
    }
  };
  const del = async (id) => { await api.delete(`/admin/blocks/${id}`); load(); };

  const fmtDate = (d) => new Date(d + "T00:00:00").toLocaleDateString("default", { weekday: "short", month: "short", day: "numeric", year: "numeric" });

  return (
    <div>
      <Header title="Block Times" subtitle="Mark days or specific time slots when you're unavailable. Blocked times can't be booked by patients." />
      <div className="grid lg:grid-cols-3 gap-6">
        <form onSubmit={save} className="bg-white border border-[#E5DEEB] rounded-2xl p-6 shadow-sm space-y-4" data-testid="block-form">
          <h3 className="font-serif text-xl text-[#2D2336]">Block a Time</h3>
          <div className="grid grid-cols-2 gap-2">
            {[{ id: "all_day", label: "Whole Day" }, { id: "slot", label: "Single Slot" }].map((m) => (
              <button type="button" key={m.id} onClick={() => setMode(m.id)} data-testid={`block-mode-${m.id}`}
                className={`py-2 rounded-full text-sm font-medium border transition-colors ${
                  mode === m.id ? "bg-[#6B4C8A] text-white border-[#6B4C8A]" : "bg-white text-[#5C5166] border-[#E5DEEB] hover:border-[#6B4C8A]"
                }`}>
                {m.label}
              </button>
            ))}
          </div>
          <div>
            <Label className="text-[#2D2336]">Date</Label>
            <Input type="date" min={todayISO()} data-testid="block-date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1" required />
          </div>
          {mode === "slot" && (
            <div>
              <Label className="text-[#2D2336]">Time Slot</Label>
              <select value={time} onChange={(e) => setTime(e.target.value)} data-testid="block-time"
                className="mt-1 block w-full h-10 rounded-md border border-[#E5DEEB] bg-white px-3 text-sm">
                <option value="">Select…</option>
                {SLOTS.map((s) => <option key={s} value={s}>{to12h(s)}</option>)}
              </select>
            </div>
          )}
          <div>
            <Label className="text-[#2D2336]">Reason (optional)</Label>
            <Input data-testid="block-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1" placeholder="e.g. Holiday, training" />
          </div>
          <button type="submit" data-testid="block-save" className="w-full bg-[#6B4C8A] text-white py-2.5 rounded-full font-medium hover:bg-[#543C6E] transition-colors flex items-center justify-center gap-2">
            <Plus className="h-4 w-4" /> Block Time
          </button>
        </form>

        <div className="lg:col-span-2 space-y-3" data-testid="blocks-list">
          {blocks.length === 0 && <div className="bg-white border border-[#E5DEEB] rounded-2xl p-10 text-center text-[#78698A]">No blocked times yet.</div>}
          {blocks.map((b) => (
            <div key={b.id} className="bg-white border border-[#E5DEEB] rounded-2xl p-5 shadow-sm flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <CalendarOff className="h-4 w-4 text-[#964545]" />
                  <h4 className="font-medium text-[#2D2336]">{fmtDate(b.date)}</h4>
                  <span className="text-xs rounded-full px-2.5 py-1 font-medium bg-[#F6E2E2] text-[#964545]">
                    {b.all_day ? "All day" : to12h(b.time)}
                  </span>
                </div>
                {b.reason && <p className="text-sm text-[#78698A] mt-1 ml-6">{b.reason}</p>}
              </div>
              <button onClick={() => del(b.id)} data-testid={`block-del-${b.id}`} className="text-[#964545] hover:opacity-70"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
